import jwt_decode from 'jwt-decode';
import Cookies from 'universal-cookie';
import axios from 'axios';

import {authApi, checkTokenApi} from './Api';
import { CONSTANTS } from 'config/constants';
import { User } from 'shared/interfaces';

let sessionExpiryInProgress = false;

interface Token {
	access_token: string;
	expires_in: number;
	jti: string;
	refresh_token: string;
	scope: string;
	token_type: string;
}

interface AccessTokenDecoded {
	client_id: string;
	exp: number;
	jti: string;
	scope: string[],
	tenant_id: string;
}

const AuthService = (() => {

	function login(username: string, password: string, type = 'DEFAULT', recaptcha = '') {
		const basicAuth = type === 'PHONE' ? process.env.REACT_APP_BASIC_PHONE_AUTH : process.env.REACT_APP_BASIC_AUTH;

		const headers = {
			'Authorization': basicAuth,
			'Recaptcha': recaptcha,
		};

		const params = {
			'grant_type': 'password',
			'username': username,
			'password': password,
			'tenant_id': process.env.REACT_APP_TENANT_ID
		};

		return authApi.post('/oauth/token', null, { params, headers });
	}

	function setToken(token: Token) {
		const cookies = new Cookies();
		const tokenDecoded: AccessTokenDecoded = jwt_decode(token.access_token);

		cookies.set(CONSTANTS.CURRENT_TOKEN, token.access_token, { path: '/', domain: process.env.REACT_APP_DOMAIN_COOKIE });
		cookies.set(CONSTANTS.TOKEN_EXPIRATION_DATE, tokenDecoded.exp, { path: '/', domain: process.env.REACT_APP_DOMAIN_COOKIE });
		cookies.set(CONSTANTS.REFRESH_TOKEN, token.refresh_token, { path: '/', domain: process.env.REACT_APP_DOMAIN_COOKIE });
		sessionExpiryInProgress = false;
	}

	function getUserData(token: string) {
		const headers = {
			'Authorization': `Bearer ${token}`,
			'Content-Type': 'application/json'
		};

		return checkTokenApi.get('/v1/user/me', { headers });
	}

	function setUserData(user: User) {
		localStorage.setItem(CONSTANTS.CURRENT_USER, JSON.stringify(user));
		sessionExpiryInProgress = false;
	}

	function readUserData() {
		const currentUser = localStorage.getItem(CONSTANTS.CURRENT_USER);
		if (currentUser) {
			return JSON.parse(currentUser);
		}
		return null;
	}

	function clearSession() {
		const cookies = new Cookies();
		localStorage.removeItem(CONSTANTS.CURRENT_USER);
		localStorage.removeItem('IS_DATA_COMPLETED');
		cookies.remove(CONSTANTS.CURRENT_TOKEN, { path: '/', domain: process.env.REACT_APP_DOMAIN_COOKIE });
		cookies.remove(CONSTANTS.TOKEN_EXPIRATION_DATE, { path: '/', domain: process.env.REACT_APP_DOMAIN_COOKIE });
		cookies.remove(CONSTANTS.REFRESH_TOKEN, { path: '/', domain: process.env.REACT_APP_DOMAIN_COOKIE });
	}

	function handleSessionExpired() {
		if (sessionExpiryInProgress) {
			return;
		}
		if (!hasLocalUser() && !getAccessToken()) {
			return;
		}
		sessionExpiryInProgress = true;

		clearSession();

		if (window.location.pathname !== '/entrar') {
			sessionStorage.setItem(CONSTANTS.SESSION_EXPIRED_KEY, '1');
			window.location.replace('/entrar');
		}
	}

	function clearCurrentUser() {
		localStorage.removeItem(CONSTANTS.CURRENT_USER);
	}

	function isAuthenticated() {
		const currentToken = getAccessToken();
		if (currentToken) {
			const expirationToken = AuthService.getTokenExpirationDate();
			if (Date.now() <= parseInt(expirationToken) * 1000) {
				return true;
			}
		}
		return false;
	}

	function hasLocalUser() {
		const currentUser = localStorage.getItem(CONSTANTS.CURRENT_USER);
		if (currentUser) {
			return true;
		}
		return false;
	}

	function getAccessToken() {
		const cookies = new Cookies();
		const currentToken = cookies.get(CONSTANTS.CURRENT_TOKEN);

		if (currentToken) {
			return currentToken;
		}
		return '';
	}

	function getTokenExpirationDate() {
		const cookies = new Cookies();
		const expirationDate = cookies.get(CONSTANTS.TOKEN_EXPIRATION_DATE);

		if (expirationDate) {
			return expirationDate;
		}
		return '';
	}

	function getRefreshToken() {
		const cookies = new Cookies();
		const refreshToken = cookies.get(CONSTANTS.REFRESH_TOKEN);

		if (refreshToken) {
			return refreshToken;
		}
		return '';
	}
	function sendWhatsappAuthCode(params: any, recaptcha?: string) {
		const headers: any = { tenant_id: process.env.REACT_APP_TENANT_ID };
		if (recaptcha) headers.Recaptcha = recaptcha;
		return authApi.post('/v3/public/user/auth/send-code', null, { headers, params });
	}

	function hasPassword(params: any) {
		const headers = { tenant_id: process.env.REACT_APP_TENANT_ID };
		return authApi.get('/v3/public/user/has-password/phone', { params, headers });
	}

	function hasUserPassword(phone: any) {
		const headers = { tenant_id: process.env.REACT_APP_TENANT_ID };
		return authApi.get(`/v3/public/user/user-key/phone/${phone}`, { headers });
	}

	function resendWhatsappAuthCode(params: any, recaptcha?: string) {
		const headers: any = { tenant_id: process.env.REACT_APP_TENANT_ID };
		if (recaptcha) headers.Recaptcha = recaptcha;
		return authApi.post('/v3/public/user/auth/resend-code', null, { headers, params });
	}

	function validateCodeForPassword(params: any, apikey: string, recaptcha: string, cookie: string) {
		const headers = { tenant_id: process.env.REACT_APP_TENANT_ID, api_key: apikey, Cookie: cookie, Recaptcha: recaptcha };
		return authApi.get('/v3/public/user/validate-code/register-password', { params, headers })
	}

	function registerPassword(payload: any, cookie: string, apikey: string) {
		const headers = { tenant_id: process.env.REACT_APP_TENANT_ID, api_key: apikey, Cookie: cookie };
		return authApi.put('/v3/public/user/register-password', payload, { headers })
	}

	// ---------- Recovery Phone Access
	function recoveryPhoneValidateEmail(params: any, recaptcha: string) {
		const headers = { recaptcha };
		return authApi.get('/v3/public/user/regain-access/email', { params, headers });
	}

	function recoveryPhoneResendCode(apiKey: string) {
		const headers = { 'api_key': apiKey };
		return authApi.get('/v3/public/user/regain-access/resend-code', { headers });
	}

	function recoveryPhoneValidateCode(params: any, apiKey: string) {
		const headers = { 'api_key': apiKey };
		return authApi.get('/v3/public/user/regain-access/validate-code', { params, headers });
	}

	function removeCodeSms(params: any) {
		const headers = { tenant_id: process.env.REACT_APP_TENANT_ID  };
		return authApi.delete('/v3/public/user/remove-code', { params, headers });
	}

	function validateEmail(apikey: string, cookie: string, params: any) {
		const headers = { tenant_id: process.env.REACT_APP_TENANT_ID, Cookie: cookie, api_key: apikey  };
		return authApi.get('/v3/public/user/validate-email/register-password', { params, headers });
	}

	return {
		login: login,
		setToken: setToken,
		getUserData: getUserData,
		setUserData: setUserData,
		readUserData: readUserData,
		clearSession: clearSession,
		clearCurrentUser: clearCurrentUser,
		isAuthenticated: isAuthenticated,
		getAccessToken: getAccessToken,
		getTokenExpirationDate: getTokenExpirationDate,
		getRefreshToken: getRefreshToken,
		hasLocalUser: hasLocalUser,
		handleSessionExpired: handleSessionExpired,

		recoveryPhoneValidateEmail,
		recoveryPhoneResendCode,
		recoveryPhoneValidateCode,
		sendWhatsappAuthCode,
		hasPassword,
		validateCodeForPassword,
		resendWhatsappAuthCode,
		registerPassword,
		hasUserPassword,
		removeCodeSms,
		validateEmail
	}
})();

export default AuthService;
